import { COLLISION_WINDOW_MS, type Lane } from "../constants";
import { isFoodKind, type EncounterKind } from "../data/encounterPatterns";
import { destinationLane, type ScheduledEncounter } from "./EncounterDirector";

export type BearCollisionPose = {
  lane: Lane;
  jumping: boolean;
  ducking: boolean;
  airborne: boolean;
};

export type CollisionEvent =
  | { type: "food"; encounter: ScheduledEncounter }
  | { type: "hazard"; encounter: ScheduledEncounter; source: "wolf" | "obstacle" }
  | { type: "avoided"; encounter: ScheduledEncounter };

export function isInHitWindow(encounter: ScheduledEncounter, elapsedMs: number): boolean {
  return Math.abs(encounter.reachAtMs - elapsedMs) <= COLLISION_WINDOW_MS;
}

export function sameLane(encounterLane: number, bearLane: Lane): boolean {
  return Math.round(encounterLane) === bearLane;
}

export function avoidedHazard(
  kind: EncounterKind,
  bear: BearCollisionPose,
): boolean {
  if (isFoodKind(kind)) {
    return true;
  }
  if (kind === "branch") {
    return bear.ducking;
  }
  return bear.airborne;
}

export function detectCollision(
  encounter: ScheduledEncounter,
  elapsedMs: number,
  bear: BearCollisionPose,
): CollisionEvent | null {
  if (encounter.consumed || !isInHitWindow(encounter, elapsedMs)) {
    return null;
  }
  if (!sameLane(destinationLane(encounter), bear.lane)) {
    return null;
  }
  if (isFoodKind(encounter.kind)) {
    return { type: "food", encounter };
  }
  if (avoidedHazard(encounter.kind, bear)) {
    // Wait until the hazard is at the bear so an early jump or duck can still be timed.
    if (elapsedMs < encounter.reachAtMs) {
      return null;
    }
    return { type: "avoided", encounter };
  }
  // Give the early half of the window to jump or duck; only hit once it arrives.
  if (elapsedMs < encounter.reachAtMs) {
    return null;
  }
  return {
    type: "hazard",
    encounter,
    source: encounter.kind === "wolf" ? "wolf" : "obstacle",
  };
}

export function detectCollisions(
  encounters: ScheduledEncounter[],
  elapsedMs: number,
  bear: BearCollisionPose,
): CollisionEvent[] {
  const events: CollisionEvent[] = [];
  for (const encounter of encounters) {
    const event = detectCollision(encounter, elapsedMs, bear);
    if (event) {
      events.push(event);
    }
  }
  return events;
}
